import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { createAsyncFilterLabelCache } from '#/modules/commonComponents/table/asyncFilterLabelCache';
import ListTablePanel from '#/modules/commonComponents/table/ListTablePanel';
import { buildFilterStatusFieldsFromColumns } from '#/modules/commonComponents/table/buildFilterStatusFields';
import { DEFAULT_LIST_SORT_FIELD_LABELS } from '#/modules/commonComponents/table/listFilterKeys';
import {
  buildStocktakeComparisonColumns,
} from '#/modules/imsMgmt/receipts/stocktake/data/stocktakeComparisonColumns';
import { getStocktakeApiErrorMessage } from '#/modules/imsMgmt/receipts/stocktake/helper/apiError';
import useStocktakeComparisonList from '#/modules/imsMgmt/receipts/stocktake/hooks/useStocktakeComparisonList';
import { hydrateWarehouseFilterLabels } from '#/modules/imsMgmt/settings/storePlace/helper/warehouseSelectOptions';
import { formatDateTimeOrDash } from '#/utils/date';

const COMPARISON_SORT_FIELD_LABELS = {
  ...DEFAULT_LIST_SORT_FIELD_LABELS,
  abnormal: '異常',
  stockNo: '庫存編號',
  materialNo: '料號',
  countedQty: '盤點量',
  currentQty: '系統量',
  differenceQty: '差異',
  countedWarehouse: '盤點倉',
  currentWarehouse: '系統倉',
  anomalies: '異常類型',
  stocktakeNo: '盤點單號',
};

/** 盤點比對 Tab panel（全寬表格；殼層提供 PageShell／標題）。 */
const StocktakeComparisonPage = () => {
  const listRef = useRef(null);
  const [warehouseLabelCache] = useState(createAsyncFilterLabelCache);
  const [labelCacheVersion, setLabelCacheVersion] = useState(0);

  const {
    rows,
    pagination,
    comparedAt,
    isLoading,
    isError,
    error,
    refetch,
    listQuery,
    setPage,
    setPageSize,
    setKeyword,
    setFilter,
    setSort,
    removeSortAt,
    resetListQuery,
  } = useStocktakeComparisonList();

  useEffect(() => {
    let cancelled = false;
    const warehouseIds = [
      ...(listQuery.filters?.countedWarehouseId ?? []),
      ...(listQuery.filters?.currentWarehouseId ?? []),
    ];

    hydrateWarehouseFilterLabels(warehouseIds, warehouseLabelCache)
      .then((didHydrate) => {
        if (!cancelled && didHydrate) {
          setLabelCacheVersion((version) => version + 1);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    listQuery.filters?.countedWarehouseId,
    listQuery.filters?.currentWarehouseId,
    warehouseLabelCache,
  ]);

  const columns = useMemo(() => {
    void labelCacheVersion;
    return buildStocktakeComparisonColumns({
      filters: listQuery.filters || {},
      onFilterChange: setFilter,
      warehouseLabelCache,
    });
  }, [listQuery.filters, setFilter, warehouseLabelCache, labelCacheVersion]);

  const filterStatusFields = useMemo(
    () => buildFilterStatusFieldsFromColumns(columns),
    [columns],
  );

  const handleSortingChange = useCallback((sorting) => {
    setSort((sorting ?? []).map((item) => ({
      id: item.id,
      desc: Boolean(item.desc),
    })));
  }, [setSort]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="mb-2 shrink-0 space-y-1">
        {comparedAt ? (
          <p className="text-sm text-slate-500">
            比對時間
            {' '}
            {formatDateTimeOrDash(comparedAt)}
          </p>
        ) : null}
        <p className="max-w-3xl text-sm text-slate-500">
          盤點量 0 不代表未盤點（NOT_COUNTED）；不比對 Location。
          修正庫存請至調整管理手動建立調整單，本頁不自動建立。
        </p>
      </div>
      <div className="table-panel min-h-0 flex-1">
        <ListTablePanel
          ref={listRef}
          columns={columns}
          rows={rows}
          pagination={pagination}
          isLoading={isLoading}
          isError={isError}
          errorMessage={
            isError
              ? getStocktakeApiErrorMessage(error, '載入盤點比對失敗')
              : undefined
          }
          onRetry={isError ? () => { refetch(); } : undefined}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          onSearch={setKeyword}
          onReset={resetListQuery}
          initialKeyword={listQuery.keyword}
          searchPlaceholder="搜尋庫存編號或物料"
          listQuery={listQuery}
          filterStatusFields={filterStatusFields}
          sortFieldLabels={COMPARISON_SORT_FIELD_LABELS}
          onFilterChange={setFilter}
          onKeywordClear={() => setKeyword('')}
          onSortRemove={removeSortAt}
          onSortingChange={handleSortingChange}
        />
      </div>
    </div>
  );
};

export default StocktakeComparisonPage;
